import { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { LoadingModal } from '@app/components/Modal';
import { Version } from '@app/components/PageFooter';
import { PageLayout } from '@app/components/PageLayout';
import { useDisplayDisconnectAlert, useScreenResolution, useShouldShowClickToDisconnect } from '@app/hooks';
import { DisplayLocation, selectAlerts } from '@app/redux/alerts';
import { selectIsPreinstall } from '@app/redux/auth';
import { getBillingAccount, selectBillingAccount } from '@app/redux/billingInfo';
import { getHasPendingTransition } from '@app/redux/changePlan';
import { selectConfig } from '@app/redux/config';
import { selectDisconnectInternet } from '@app/redux/disconnect';
import {
  EditableCardType,
  ScrubAddressModalType,
  selectScrubbedAddress,
  setCloseAfterReload as setCloseAfterReloadAction,
  setEditingCard as setEditingCardAction,
  setShowScrubModal,
} from '@app/redux/scrubbedAddress';
import { selectUserInfo } from '@app/redux/userInfo';
import { setProfilePageStatistics } from '@app/redux/utils';

import { AccountType, BillingAccount, Platform } from '@mfe/shared/schema-types';

import CurrentTab from './components/CurrentTab';
import Navigation from './components/Navigation';
import ProfileAlerts from './components/ProfileAlerts';
import ProfileHeader from './components/ProfileHeader';
import ProfileModal from './components/ProfileModal';
import SignoutButton from './components/SignoutButton';
import { getNextBillCycleDate } from '../../utils';
import { ProfileTab } from './types';

import styles from './styles.module.scss';

const AccountSettings = ({
  navigateToChangePlan,
  navigateToScheduleInstallation,
}: {
  navigateToChangePlan: () => void;
  navigateToScheduleInstallation: () => void;
}): JSX.Element | null => {
  const dispatch = useDispatch();

  const billingAccount: BillingAccount = useSelector(selectBillingAccount);
  const nextBillingCycleDate = getNextBillCycleDate(billingAccount);
  const { success: hasDisconnected } = useSelector(selectDisconnectInternet);
  const displayDisconnectAlert = useDisplayDisconnectAlert();
  const hasPreinstall = useSelector(selectIsPreinstall);

  const showClickToDisconnect = useShouldShowClickToDisconnect();

  useEffect(() => {
    if (hasDisconnected && showClickToDisconnect && !hasPreinstall) {
      displayDisconnectAlert();
    }
  }, [hasDisconnected, showClickToDisconnect, hasPreinstall, displayDisconnectAlert]);

  const { isMedium, isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isMedium || isSmall || isExtraSmall;

  const setShowModal = useCallback(
    (modalType: ScrubAddressModalType | null) => dispatch(setShowScrubModal(modalType)),
    [dispatch]
  );

  const setCloseAfterReload = useCallback(
    (payload: boolean) => dispatch(setCloseAfterReloadAction(payload)),
    [dispatch]
  );

  const setEditingCard = useCallback(
    (editingCard: EditableCardType) => dispatch(setEditingCardAction(editingCard)),
    [dispatch]
  );

  const { editingCard, closeAfterReload } = useSelector(selectScrubbedAddress);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const [currentTab, setCurrentTab] = useState(
    accountType === AccountType.Residential ? ProfileTab.ResidentialAccount : ProfileTab.SMBAccount
  );
  const [pendingTab, setPendingTab] = useState<ProfileTab | null>(null);
  const [restrictSwitchTabs, setRestrictSwitchTabs] = useState<boolean>(false);

  const { platform } = useSelector(selectConfig);

  const isMobileApp = platform === Platform.Ios || platform === Platform.Android;

  useEffect(() => {
    setNavigationTab();

    function setNavigationTab() {
      setNavigationTabFromStorage(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, ProfileTab.InternetPlan);
    }

    function setNavigationTabFromStorage(storageKey: string, tab: ProfileTab) {
      const navigatedFrom = sessionStorageManager.getItem(storageKey);
      if (navigatedFrom) {
        setCurrentTab(tab);
        sessionStorageManager.removeItem(storageKey);
      }
    }
  }, []);

  useEffect(() => {
    dispatch(setProfilePageStatistics());
    dispatch(getHasPendingTransition());
    if (!nextBillingCycleDate) {
      dispatch(getBillingAccount());
    }
  }, [nextBillingCycleDate, dispatch]);

  useEffect(() => {
    if (accountType === AccountType.Smb && currentTab === ProfileTab.ResidentialAccount) {
      setCurrentTab(ProfileTab.SMBAccount);
    }
    if (accountType === AccountType.Residential && currentTab === ProfileTab.SMBAccount) {
      setCurrentTab(ProfileTab.ResidentialAccount);
    }
  }, [accountType, currentTab]);

  const commonProps = {
    setEditingCard,
    setCloseAfterReload,
    setShowModal,
    setRestrictSwitchTabs,
  };

  const { alerts } = useSelector(selectAlerts);
  const { wasUpdateSuccessful, error } = useSelector(selectUserInfo);
  const profileAlerts = alerts.filter((alert) => alert.displayLocation === DisplayLocation.GLOBAL);

  const showAlertsSection = wasUpdateSuccessful || error || profileAlerts.length > 0;

  return (
    <PageLayout>
      <div className={styles['pageHeader']}>
        {showAlertsSection && <ProfileAlerts alerts={profileAlerts} />}
        <ProfileHeader />
      </div>
      <div className={styles['bodyGrid']}>
        <Navigation
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          restrictSwitchTabs={restrictSwitchTabs}
          setShowModal={setShowScrubModal}
          setPendingTab={setPendingTab}
        />
        <Suspense fallback={null}>
          <CurrentTab
            {...commonProps}
            editingCard={editingCard}
            currentTab={currentTab}
            closeAfterReload={closeAfterReload}
            navigateToChangePlan={navigateToChangePlan}
            navigateToScheduleInstallation={navigateToScheduleInstallation}
          />
          {isMobileApp && (
            <div className={styles['versionContainer']}>
              <Version />
            </div>
          )}
          {isMobile && <SignoutButton />}
        </Suspense>
      </div>
      <Suspense fallback={<LoadingModal isOpen={true} />}>
        <ProfileModal
          {...commonProps}
          pendingTab={pendingTab}
          setPendingTab={setPendingTab}
          setCurrentTab={setCurrentTab}
        />
      </Suspense>
    </PageLayout>
  );
};

export default AccountSettings;
